import { useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import TabPanel from '../../TabPanel';
import { type SettingsModalTabProps } from '../../types';

import { GetAccessAuditEventsDocument } from '@/graphql/generated/graphql';
import DateTimeFormatter from '@/shared-components/DateTimeFormatter';

const PAGE_SIZE = 50;

const EVENT_LABELS: Record<string, string> = {
  grant_added: 'Permission granted',
  grant_removed: 'Permission removed',
  role_created: 'Role created',
  role_changed: 'Role changed',
  role_deleted: 'Role deleted',
  role_assigned: 'Role assigned',
  role_unassigned: 'Role removed',
  request_created: 'Access requested',
  request_approved: 'Request approved',
  request_rejected: 'Request rejected',
};

const EVENT_COLOR: Record<string, 'success' | 'error' | 'info' | 'warning' | 'default'> = {
  grant_added: 'success',
  grant_removed: 'error',
  role_created: 'info',
  role_changed: 'info',
  role_deleted: 'error',
  role_assigned: 'success',
  role_unassigned: 'warning',
  request_created: 'default',
  request_approved: 'success',
  request_rejected: 'error',
};

const summarize = (payload: unknown): string => {
  if (!payload || typeof payload !== 'object') return '—';
  const data = payload as Record<string, unknown>;

  const describeGrants = (value: unknown) =>
    Array.isArray(value)
      ? value
          .map((grant) => {
            const g = grant as Record<string, unknown>;
            const scope = g.projectId ? ` (project ${g.projectId})` : '';
            return `${g.action} ${g.entity}${scope}`;
          })
          .join(', ')
      : '';

  const parts: string[] = [];
  const added = describeGrants(data.added);
  const removed = describeGrants(data.removed);
  if (added) parts.push(`+ ${added}`);
  if (removed) parts.push(`− ${removed}`);
  if (data.role_name) parts.push(String(data.role_name));
  if (data.name && !data.role_name) parts.push(String(data.name));
  if (data.action && data.entity) parts.push(`${data.action} ${data.entity}`);

  return parts.length > 0 ? parts.join(' · ') : '—';
};

const AuditTab: React.FC<SettingsModalTabProps> = ({ parentTab, index }) => {
  const [offset, setOffset] = useState(0);
  const { data, loading } = useQuery(GetAccessAuditEventsDocument, {
    variables: { limit: PAGE_SIZE, offset },
    fetchPolicy: 'cache-and-network',
  });

  const events = data?.getAccessAuditEvents ?? [];

  return (
    <TabPanel value={parentTab} index={index} height="100%" containerSx={{ height: '100%' }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={800}>
          Access audit trail
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every permission and role change in this company, newest first.
        </Typography>

        {loading && events.length === 0 ? (
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading audit events…
            </Typography>
          </Box>
        ) : events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {offset === 0 ? 'No access changes recorded yet.' : 'No further events.'}
          </Typography>
        ) : (
          <TableContainer sx={{ border: '1px solid rgba(0,0,0,0.12)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, bgcolor: 'rgba(0,0,0,0.03)' }}>When</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: 'rgba(0,0,0,0.03)' }}>Event</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: 'rgba(0,0,0,0.03)' }}>By</TableCell>
                  <TableCell sx={{ fontWeight: 800, bgcolor: 'rgba(0,0,0,0.03)' }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <DateTimeFormatter rawDate={event.createdAt} includeTime />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={EVENT_LABELS[event.event] ?? event.event}
                        color={EVENT_COLOR[event.event] ?? 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{event.actorEmployment?.fullName ?? 'System'}</TableCell>
                    <TableCell>{summarize(event.payload)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button
            size="small"
            disabled={offset === 0 || loading}
            onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            size="small"
            disabled={events.length < PAGE_SIZE || loading}
            onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
          >
            Next
          </Button>
        </Box>
      </Box>
    </TabPanel>
  );
};

export default AuditTab;
